import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import { 
  Save, ArrowLeft, User, Wrench, Shield, Search, Plus, Trash2, List, 
  DollarSign, ChevronLeft, ChevronRight, Camera, UserCircle
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';

const LaborDetails = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const [activeTab, setActiveTab] = useState('MAIN');
  const fileInputRef = useRef(null);
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';  
  
  const [data, setData] = useState({
    laborcode: '', personid: '', craft_code: '', status: 'ACTIVE', actual_rate: 0,
    org_id: sessionOrg, site_id: sessionSite, image_url: '',
    person: { displayname: '', email: '', title: '' },
    crafts_list: [] 
  });
  
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [lookup, setLookup] = useState({ isOpen: false, field: '', type: '', targetIdx: null, title: '', filters: {} });

  const loadData = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/labor/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(res.data);
      
      // جلب النافيجيشن
      const navRes = await axios.get(`/api/labor/${id}/navigation${location.search}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNav(navRes.data);
    } catch (err) { 
      toast.error(t('common.load_error', "Error loading record")); 
    }
  }, [id, isNew, location.search, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      const token = localStorage.getItem('token'); 
      await axios.post('/api/labor/save', data, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('common.save_success', "Saved successfully"), { id: loadId });
      if (isNew) navigate(`/labors/${data.laborcode}${location.search}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.save_fail', "Save failed"), { id: loadId });
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('labor.details.delete_warn', 'You will not be able to recover this record!'),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/labor/${data.laborcode}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', "Deleted successfully"));
        navigate(`/labors${location.search}`); 
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.delete_fail', "Delete failed"));
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || isNew) return; 
    
    const formData = new FormData();
    formData.append('file', file);
    
    const loadId = toast.loading(t('common.uploading', "Uploading image..."));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/labor/${data.laborcode}/upload-image`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setData({ ...data, image_url: res.data.url });
      toast.success(t('common.upload_success', "Image uploaded!"), { id: loadId });
    } catch (err) {
      toast.error(t('common.upload_fail', "Upload failed"), { id: loadId });
    }
  };

  const goTo = (targetId) => targetId && navigate(`/labors/${targetId}${location.search}`);

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    let extraFilters = { site_id: data.site_id, org_id: data.org_id };
    if (lineIdx !== null && data.crafts_list && data.crafts_list[lineIdx]?.craft_code) {
      extraFilters.craft_code = data.crafts_list[lineIdx].craft_code;
    }
    setLookup({ isOpen: true, title, type: defaultType, field: fieldName, filters: extraFilters, targetIdx: lineIdx });
  };

  const addNewCraftRow = () => setData({ ...data, crafts_list: [...(data.crafts_list || []), { craft_code: '', skill_level: '', standard_rate: 0, is_default: false }] });
  const removeCraftRow = (index) => {
    const newList = [...(data.crafts_list || [])];
    newList.splice(index, 1);
    setData({ ...data, crafts_list: newList });
  };

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] tracking-tighter uppercase ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* --- 1. TOP TOOLBAR (Control Center) --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/labors${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-blue-200 transition-colors">
            {isRTL ? <ChevronRight size={18}/> : <ArrowLeft size={18}/>}
          </button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all">
            <Save size={14}/> {t('common.save', 'SAVE')}
          </button>
          
          {!isNew && (
            <>
              <div className="w-[1px] h-5 bg-white/20 mx-1"></div>
              <button onClick={handleDelete} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors" title={t('common.delete')}>
                <Trash2 size={16}/>
              </button>
            </>
          )}
        </div>
      </div>

      {/* --- SPLIT LAYOUT (SIDEBAR + MAIN CONTENT) --- */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* 🎯 SIDEBAR (PROFILE CARD) */}
        <div className={`w-80 bg-white border-gray-200 z-10 flex flex-col items-center py-8 px-6 shadow-[0_0_15px_rgba(0,0,0,0.03)] ${isRTL ? 'border-l' : 'border-r'}`}>
          
          {/* Avatar/Image Section */}
          <div className="relative group mb-6">
            <div className="w-44 h-44 rounded-full border-4 border-white shadow-xl overflow-hidden bg-slate-50 flex items-center justify-center ring-1 ring-gray-100">
              {data.image_url ? (
                <img src={data.image_url} alt="Labor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <UserCircle size={80} className="text-gray-300" strokeWidth={1} />
              )}
            </div>
            {!isNew && (
              <div 
                className="absolute inset-0 bg-black/50 rounded-full flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-300 backdrop-blur-sm border-4 border-transparent"
                onClick={() => fileInputRef.current.click()}
              >
                <Camera size={32} className="text-white mb-2 shadow-lg" />
                <span className="text-white text-[10px] font-black tracking-widest uppercase">{t('common.upload', 'UPLOAD')}</span>
              </div>
            )}
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
          </div>

          {/* Labor Basic Info */}
          <div className="w-full space-y-5 text-center">
            <div className="flex flex-col">
              <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('labor.fields.laborcode', 'LABOR CODE')}</label>
              <input 
                className={`text-2xl font-black text-[#1a3353] outline-none border-b-2 border-dashed border-gray-200 focus:border-blue-500 bg-transparent uppercase text-center transition-colors pb-1 ${!isNew && 'opacity-80 border-transparent'}`} 
                value={data.laborcode} 
                onChange={e => setData({...data, laborcode: e.target.value.toUpperCase()})} 
                disabled={!isNew} 
                placeholder="NEW_LABOR"
              />
            </div>
            
            <div className="flex flex-col">
              <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('labor.fields.fullname', 'PERSON NAME')}</label>
              <span className="text-blue-600 font-black text-lg truncate px-2" title={data.person?.displayname}>{data.person?.displayname || '---'}</span>
            </div>

            {/* Status Badge */}
            <div className="pt-2">
              <span className={`px-5 py-1.5 rounded-full text-[10px] shadow-sm border font-black uppercase tracking-widest ${ data.status === 'ACTIVE' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200' }`}>
                {data.status || 'INACTIVE'}
              </span>
            </div>
          </div>
        </div>

        {/* 🎯 MAIN CONTENT AREA (TABS + DATA) */}
        <div className="flex-1 flex flex-col bg-[#f3f4f6] overflow-hidden">
          
          {/* TABS LAYOUT */}
          <div className="flex bg-white shadow-sm overflow-x-auto no-scrollbar px-4 pt-2">
            {['MAIN', 'CRAFTS', 'LOG'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} 
                className={`px-8 py-3 border-b-4 transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest whitespace-nowrap rounded-t-md
                ${activeTab === tab ? 'border-orange-500 text-[#1a3353] bg-orange-50/30' : 'border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50'}`}>
                {tab === 'CRAFTS' && <Wrench size={13}/>} {t(`labor.tabs.${tab.toLowerCase()}`, tab)}
              </button>
            ))}
          </div>

          {/* CONTENT AREA */}
          <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
            <div className="max-w-5xl mx-auto">
              {activeTab === 'MAIN' && (
                <div className="bg-white rounded-lg shadow-sm p-8 animate-in fade-in duration-500 border border-gray-100">
                  <div className="grid grid-cols-2 gap-16">
                    <div className="space-y-6">
                      <h3 className="text-[#1a3353] border-b border-gray-100 pb-3 flex items-center gap-2 font-black italic text-sm">
                        <div className="w-1.5 h-4 bg-blue-600 rounded-full"></div> {t('labor.sections.basic', 'Basic Information')}
                      </h3>
                      
                      <div className="flex flex-col border-b border-gray-100 group focus-within:border-blue-500 transition-all py-2">
                        <label className="text-gray-400 text-[10px] font-bold tracking-widest">{t('labor.fields.personid', 'Person Reference')}</label>
                        <div className={`flex justify-between items-center h-7 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="text-blue-900 font-black text-[12px]">{data.personid || '---'}</span>
                          <Search size={16} className="text-blue-500 cursor-pointer hover:scale-125 transition-transform" onClick={() => handleSearch('personid', t('labor.lookup.person'), 'persons')}/>
                        </div>
                      </div>
                      
                      <div className="flex flex-col border-b border-gray-100 group focus-within:border-blue-500 transition-all py-2">
                        <label className="text-gray-400 text-[10px] font-bold tracking-widest">{t('labor.fields.primary_craft', 'Primary Craft')}</label>
                        <div className={`flex justify-between items-center h-7 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="text-blue-900 font-black text-[12px]">{data.craft_code || '---'}</span>
                          <Search size={16} className="text-blue-500 cursor-pointer hover:scale-125 transition-transform" onClick={() => handleSearch('craft_code', t('labor.lookup.craft'), 'crafts')}/>
                        </div>
                      </div>
                      
                      <div className="flex flex-col border-b border-gray-100 group focus-within:border-blue-500 transition-all py-2">
                        <label className="text-gray-400 text-[10px] font-bold tracking-widest">{t('labor.fields.actual_rate', 'Actual Rate (Hourly)')}</label>
                        <div className={`flex items-center gap-2 h-7 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <DollarSign size={14} className="text-green-600" />
                          <input 
                            type="number" 
                            className={`outline-none text-blue-900 font-black bg-transparent w-full text-[12px] ${isRTL ? 'text-right' : 'text-left'}`}
                            value={data.actual_rate} 
                            onChange={e => setData({...data, actual_rate: parseFloat(e.target.value) || 0})} 
                          />
                        </div>
                      </div>                  
                    </div>
                    
                    <div className="space-y-6">
                      <h3 className="text-[#1a3353] border-b border-gray-100 pb-3 flex items-center gap-2 font-black italic text-sm">
                        <div className="w-1.5 h-4 bg-orange-500 rounded-full"></div> {t('labor.sections.status', 'Status & Organization')}
                      </h3>
                      <div className="p-6 bg-[#f8fafc] rounded-xl border border-gray-200 space-y-4 text-[12px]">
                          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                            <span className="text-gray-500 font-bold">{t('common.status', 'Current Status')}:</span> 
                            <select 
                                className="bg-white text-blue-900 px-3 py-1 rounded font-black outline-none border border-gray-300 shadow-sm cursor-pointer hover:border-blue-400 transition-colors"
                                value={data.status}
                                onChange={(e) => setData({...data, status: e.target.value})}
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                            </select>
                          </div>
                          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                            <span className="text-gray-500 font-bold">{t('common.site', 'Site ID')}:</span> 
                            <span className="text-blue-900 font-black bg-white px-3 py-1 rounded border shadow-sm">{data.site_id || '---'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500 font-bold">{t('common.org', 'Organization')}:</span> 
                            <span className="text-blue-900 font-black bg-white px-3 py-1 rounded border shadow-sm">{data.org_id || '---'}</span>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'CRAFTS' && (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-in slide-in-from-right duration-300 border border-gray-100">
                  <div className="bg-slate-50 p-4 border-b flex justify-between items-center">
                    <h3 className="text-blue-900 flex items-center gap-2 font-black italic text-[12px]">
                      <List size={16} className="text-orange-500"/> {t('labor.sections.additional_crafts', 'Additional Crafts & Specialized Skills')}
                    </h3>
                    <button onClick={addNewCraftRow} className="bg-[#1a3353] text-white px-5 py-2 rounded text-[10px] flex items-center gap-2 hover:bg-blue-900 transition-all shadow-md font-black active:scale-95">
                      <Plus size={14}/> {t('labor.actions.add_craft', 'ADD CRAFT LINE')}
                    </button>
                  </div>
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-[#f1f5f9] border-b text-gray-500 font-black uppercase text-[10px] tracking-widest">
                      <tr>
                        <th className="p-4 w-12 text-center">#</th>
                        <th className="p-4 border-x border-gray-200">{t('labor.fields.craft', 'Craft Definition')}</th>
                        <th className="p-4 border-x border-gray-200">{t('labor.fields.skill', 'Skill Level')}</th>
                        <th className="p-4 border-x border-gray-200 text-center">{t('labor.fields.rate', 'Hourly Rate')}</th>
                        <th className="p-4 border-x border-gray-200 text-center">{t('labor.fields.primary', 'Primary?')}</th>
                        <th className="p-4 w-16 text-center">{t('common.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.crafts_list?.map((row, idx) => (
                        <tr key={idx} className="hover:bg-blue-50/30 transition-colors group font-bold">
                          <td className="p-4 text-gray-400 italic text-center">{idx + 1}</td>
                          <td className="p-4 border-x border-gray-50 text-blue-900">
                            <div className={`flex justify-between items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <span className="truncate">{row.craft_code || '---'}</span>
                              <Search size={16} className="text-blue-400 cursor-pointer hover:text-blue-900 opacity-0 group-hover:opacity-100 transition-all hover:scale-125" 
                                      onClick={() => handleSearch('row_craft', t('labor.lookup.craft'), 'crafts', idx)}/>
                            </div>
                          </td>
                          <td className="p-4 border-x border-gray-50">
                            <div className={`flex justify-between items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <span className={row.skill_level ? 'text-orange-600' : 'text-gray-300'}>{row.skill_level || 'NOT SET'}</span>
                                <Search size={16} className="text-orange-400 cursor-pointer hover:text-orange-900 opacity-0 group-hover:opacity-100 transition-all hover:scale-125" 
                                        onClick={() => handleSearch('row_skill', t('labor.lookup.skill'), 'craftskills', idx)}/>
                            </div>
                          </td>
                          <td className="p-4 border-x border-gray-50 text-center font-mono text-green-700 bg-green-50/30">
                            <div className={`flex items-center justify-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <DollarSign size={12} className="text-green-500"/>
                              <input 
                                type="number" 
                                className="w-24 bg-transparent outline-none text-center font-black text-[12px]"
                                value={row.standard_rate || 0}
                                onChange={e => {
                                  const newList = [...data.crafts_list];
                                  newList[idx].standard_rate = parseFloat(e.target.value) || 0;
                                  setData({...data, crafts_list: newList});
                                }} 
                              />
                            </div>
                          </td>
                          <td className="p-4 border-x border-gray-50 text-center">
                            <input 
                              type="checkbox" 
                              className="w-5 h-5 cursor-pointer accent-orange-500" 
                              checked={row.is_default || false} 
                              onChange={e => {
                                const isChecked = e.target.checked;
                                const newList = [...data.crafts_list];
                                newList.forEach((item, i) => item.is_default = (i === idx ? isChecked : false));
                                const newBaseData = { ...data, crafts_list: newList };
                                if (isChecked) {
                                  newBaseData.actual_rate = row.standard_rate || 0;
                                  newBaseData.craft_code = row.craft_code;
                                }
                                setData(newBaseData);
                              }}
                            />
                          </td>
                          <td className="p-4 text-center">
                            <button onClick={() => removeCraftRow(idx)} className="text-gray-300 hover:text-red-500 transition-colors hover:bg-red-50 p-1.5 rounded-md">
                              <Trash2 size={18}/>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!data.crafts_list || data.crafts_list.length === 0) && (
                    <div className="p-20 text-center text-gray-300 italic font-black uppercase tracking-widest bg-gray-50/50">
                      {t('labor.messages.no_crafts', 'No Craft Associations Found')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Lookup System */}
      <UniversalLookup 
        {...lookup} 
        isOpen={lookup.isOpen} 
        onClose={() => setLookup({...lookup, isOpen:false})}
        onSelect={(row) => {
          if (lookup.field === 'personid') {
            setData({ ...data, personid: row.personid, person: row });
          } 
          else if (lookup.field === 'row_craft') {
            const newList = [...data.crafts_list];
            if (row.skills && row.skills.length > 0) {
              const skillsToAdd = row.skills.map(s => ({
                craft_code: row.craft_code,
                skill_level: s.skill_level,
                standard_rate: s.standard_rate,
                is_default: false
              }));
              newList.splice(lookup.targetIdx, 1, ...skillsToAdd);
              setData({ ...data, crafts_list: newList });
            } else {
              newList[lookup.targetIdx] = {
                ...newList[lookup.targetIdx],
                craft_code: row.craft_code,
                standard_rate: row.standard_rate || 0
              };
              setData({ ...data, crafts_list: newList });
            }
          } 
          else if (lookup.field === 'row_skill') {
            const newList = [...data.crafts_list];
            newList[lookup.targetIdx].skill_level = row.skill_level;
            newList[lookup.targetIdx].standard_rate = row.standard_rate;
            setData({ ...data, crafts_list: newList });
          }
          else {
            setData({ ...data, [lookup.field]: row.craft_code || row.value });
          }
          setLookup({ ...lookup, isOpen: false });
        }}
      />
    </div>
  );
};

export default LaborDetails;